/**
 * SystemConfigTab
 *
 * System configuration settings tab showing binary status and environment variables.
 */

import { AlertTriangle, CheckCircle, Eye, EyeOff, Loader2, Minus, Plus, RefreshCw, RotateCcw, XCircle } from "lucide-react"
import { observer } from "mobx-react"
import { useEffect, useState } from "react"
import { type RuntimeBinaryStatus, ensureBinary, getStatuses } from "../../nestAPIs/binaries"
import { isCodeModuleAvailable } from "../../nestAPIs/capabilities"
import { type HarnessStatusMap, getHarnessStatuses, isHarnessStatusApiAvailable } from "../../nestAPIs/harnessStatus"
import { GLOBAL_SYSTEM_PROMPT_MAX_LENGTH, permissionModeOptions } from "../../persistence/personalSettingsStore"
import type { CodeStore } from "../../store/store"
import { getHarnessAuthTypeLabel, getHarnessDisplayName, toHarnessStatusView } from "./harnessStatusUtils"

interface KeyValuePair {
    key: string
    value: string
}

const KeyValueEditor = ({
    pairs,
    onChange,
    keyPlaceholder = "Key",
    valuePlaceholder = "Value",
    secretMode = false,
}: {
    pairs: KeyValuePair[]
    onChange: (pairs: KeyValuePair[]) => void
    keyPlaceholder?: string
    valuePlaceholder?: string
    secretMode?: boolean
}) => {
    const [revealedIndices, setRevealedIndices] = useState<Set<number>>(new Set())

    const toggleReveal = (index: number) => {
        const newRevealed = new Set(revealedIndices)
        if (newRevealed.has(index)) {
            newRevealed.delete(index)
        } else {
            newRevealed.add(index)
        }
        setRevealedIndices(newRevealed)
    }

    const addPair = () => {
        onChange([...pairs, { key: "", value: "" }])
    }

    const removePair = (index: number) => {
        onChange(pairs.filter((_, i) => i !== index))
    }

    const updatePair = (index: number, field: "key" | "value", value: string) => {
        const newPairs = [...pairs]
        newPairs[index] = { ...newPairs[index], [field]: value }
        onChange(newPairs)
    }

    return (
        <div className="flex flex-col gap-2">
            {pairs.map((pair, index) => (
                <div key={index} className="flex items-center gap-2 max-[600px]:grid max-[600px]:grid-cols-[minmax(0,1fr)_2.75rem]">
                    <input
                        type="text"
                        placeholder={keyPlaceholder}
                        value={pair.key}
                        onChange={(e) => updatePair(index, "key", e.target.value)}
                        className="input min-h-10 flex-1 bg-base-200 border border-border p-2 px-3 text-sm font-mono rounded-none max-[600px]:col-span-2 max-[600px]:min-h-11 max-[600px]:w-full"
                    />
                    <div className="relative min-w-0 flex-1">
                        <input
                            type={secretMode && !revealedIndices.has(index) ? "password" : "text"}
                            placeholder={valuePlaceholder}
                            value={pair.value}
                            onChange={(e) => updatePair(index, "value", e.target.value)}
                            className="input min-h-10 w-full bg-base-200 border border-border p-2 px-3 pr-10 text-sm font-mono rounded-none max-[600px]:min-h-11"
                        />
                        {secretMode && pair.value && (
                            <button
                                type="button"
                                onClick={() => toggleReveal(index)}
                                className="btn absolute right-1 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center bg-transparent text-muted hover:text-base-content"
                            >
                                {revealedIndices.has(index) ? <EyeOff size={14} /> : <Eye size={14} />}
                            </button>
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={() => removePair(index)}
                        className="btn w-8 h-8 flex items-center justify-center bg-error/10 hover:bg-error text-error hover:text-error-content transition-colors max-[600px]:h-11 max-[600px]:w-11"
                    >
                        <Minus size={14} />
                    </button>
                </div>
            ))}
            <button
                type="button"
                onClick={addPair}
                className="btn flex min-h-10 items-center justify-center gap-2 p-2 bg-base-200 hover:bg-base-300 text-muted hover:text-base-content transition-colors text-sm border border-dashed border-border max-[600px]:min-h-11"
            >
                <Plus size={14} />
                Add Variable
            </button>
        </div>
    )
}

const HarnessStatusSection = () => {
    const [statuses, setStatuses] = useState<HarnessStatusMap>({})
    const [isLoading, setIsLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)

    const refresh = async () => {
        setIsLoading(true)
        try {
            const result = await getHarnessStatuses()
            setStatuses(result.statuses)
            setError(result.error)
        } finally {
            setIsLoading(false)
        }
    }

    useEffect(() => {
        if (!isHarnessStatusApiAvailable()) {
            setIsLoading(false)
            return
        }

        refresh()
    }, [])

    if (!isCodeModuleAvailable()) return null

    const entries = Object.entries(statuses).sort((a, b) => getHarnessDisplayName(a[0]).localeCompare(getHarnessDisplayName(b[0])))

    return (
        <section>
            <div className="flex items-center justify-between mb-1">
                <h3 className="text-base font-semibold text-base-content">Harness Status</h3>
                <button
                    type="button"
                    onClick={refresh}
                    disabled={isLoading}
                    className="btn w-8 h-8 flex items-center justify-center bg-transparent hover:bg-base-300 text-muted hover:text-base-content transition-colors disabled:opacity-50 max-[600px]:h-11 max-[600px]:w-11"
                    title="Refresh"
                >
                    <RefreshCw size={14} className={isLoading ? "animate-spin" : ""} />
                </button>
            </div>
            <p className="text-sm text-muted mb-4">Install and authentication status for each configured harness CLI.</p>

            {isLoading && entries.length === 0 && (
                <div className="flex items-center gap-2 p-3 text-sm text-muted">
                    <Loader2 size={16} className="animate-spin" />
                    Loading...
                </div>
            )}

            {error && <div className="p-3 border border-warning/30 bg-warning/10 text-sm text-warning">{error}</div>}

            {!isLoading && entries.length === 0 && !error && (
                <div className="p-3 border border-border bg-base-200/40 text-sm text-muted">No harnesses registered.</div>
            )}

            {entries.length > 0 && (
                <div className="flex flex-col gap-2">
                    {entries.map(([harnessId, status]) => {
                        const view = toHarnessStatusView(status)
                        const toneClass =
                            view.tone === "success"
                                ? "bg-success/15 text-success border-success/30"
                                : view.tone === "warning"
                                  ? "bg-warning/15 text-warning border-warning/30"
                                  : "bg-error/15 text-error border-error/30"
                        const StatusIcon = view.tone === "success" ? CheckCircle : view.tone === "warning" ? AlertTriangle : XCircle

                        return (
                            <div key={harnessId} className="p-3 bg-base-200/40 border border-border">
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <p className="text-sm font-medium text-base-content">{getHarnessDisplayName(harnessId)}</p>
                                        <p className="text-xs text-muted mt-1">{view.subtitle}</p>
                                    </div>
                                    <div className={`px-2 py-1 border text-xs font-medium shrink-0 flex items-center gap-1.5 ${toneClass}`}>
                                        <StatusIcon size={12} />
                                        <span>{view.label}</span>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 mt-2 text-xs text-muted">
                                    <span className="px-1.5 py-0.5 border border-border bg-base-100 font-mono">{status.version ?? "version unknown"}</span>
                                    <span className="px-1.5 py-0.5 border border-border bg-base-100">{getHarnessAuthTypeLabel(status.authType)}</span>
                                </div>
                            </div>
                        )
                    })}
                </div>
            )}
        </section>
    )
}

const RuntimeBinaryRow = ({ binary, onRefresh }: { binary: RuntimeBinaryStatus; onRefresh: () => void }) => {
    const [isVerifying, setIsVerifying] = useState(false)

    const handleVerify = async () => {
        setIsVerifying(true)
        try {
            await ensureBinary(binary.name)
            onRefresh()
        } finally {
            setIsVerifying(false)
        }
    }

    const getStatusIcon = () => {
        if (isVerifying) {
            return <Loader2 size={16} className="text-muted animate-spin" />
        }
        if (binary.status === "available") {
            return <CheckCircle size={16} className="text-success" />
        }
        if (binary.status === "error") {
            return <XCircle size={16} className="text-error" />
        }
        return <Minus size={16} className="text-muted" />
    }

    const getStatusText = () => {
        if (isVerifying) return "Checking..."
        if (binary.status === "available") return binary.path
        if (binary.status === "error") return binary.error
        return binary.error ?? "Not found on PATH"
    }

    return (
        <div className="flex items-center justify-between p-3 bg-base-200 border border-border min-w-0">
            <div className="flex items-center gap-3 min-w-0 flex-1">
                <div className="shrink-0">{getStatusIcon()}</div>
                <div className="min-w-0">
                    <p className="text-sm font-medium text-base-content">
                        {binary.displayName} <span className="text-muted font-normal">v{binary.version}</span>
                    </p>
                    <p
                        className={`text-xs mt-1 font-mono truncate ${binary.status === "error" ? "text-error" : "text-muted"}`}
                        title={getStatusText() ?? undefined}
                    >
                        {getStatusText()}
                    </p>
                </div>
            </div>
            <button
                type="button"
                onClick={handleVerify}
                disabled={isVerifying}
                className="btn w-8 h-8 shrink-0 flex items-center justify-center bg-transparent hover:bg-base-300 text-muted hover:text-base-content transition-colors disabled:opacity-50 max-[600px]:h-11 max-[600px]:w-11"
                title="Re-check"
            >
                <RotateCcw size={14} className={isVerifying ? "animate-spin" : ""} />
            </button>
        </div>
    )
}

const RuntimeBinariesSection = () => {
    const [statuses, setStatuses] = useState<RuntimeBinaryStatus[]>([])
    const [isLoading, setIsLoading] = useState(true)

    const refresh = async () => {
        setIsLoading(true)
        try {
            const result = await getStatuses()
            setStatuses(result)
        } finally {
            setIsLoading(false)
        }
    }

    useEffect(() => {
        if (!isCodeModuleAvailable()) {
            setIsLoading(false)
            return
        }

        refresh()
    }, [])

    if (!isCodeModuleAvailable()) return null

    return (
        <section>
            <div className="flex items-center justify-between mb-1">
                <h3 className="text-base font-semibold text-base-content">Runtime Binaries</h3>
                <button
                    type="button"
                    onClick={refresh}
                    disabled={isLoading}
                    className="btn w-8 h-8 flex items-center justify-center bg-transparent hover:bg-base-300 text-muted hover:text-base-content transition-colors disabled:opacity-50 max-[600px]:h-11 max-[600px]:w-11"
                    title="Refresh"
                >
                    <RefreshCw size={14} className={isLoading ? "animate-spin" : ""} />
                </button>
            </div>
            <p className="text-sm text-muted mb-4">Binaries visible to the Nest runtime.</p>
            {isLoading && statuses.length === 0 ? (
                <div className="flex items-center gap-2 p-3 text-sm text-muted">
                    <Loader2 size={16} className="animate-spin" />
                    Loading...
                </div>
            ) : (
                <div className="flex flex-col gap-2">
                    {statuses.map((binary) => (
                        <RuntimeBinaryRow key={binary.name} binary={binary} onRefresh={refresh} />
                    ))}
                </div>
            )}
        </section>
    )
}

interface SystemConfigTabProps {
    store: CodeStore
}

export const SystemConfigTab = observer(({ store }: SystemConfigTabProps) => {
    const personalSettings = store.personalSettingsStore
    const currentEnvVars = personalSettings?.settings.current.envVars ?? {}
    const savedGlobalSystemPrompt = personalSettings?.settings.current.globalSystemPrompt ?? ""

    // Convert Record to array of pairs for editing
    const [envVarPairs, setEnvVarPairs] = useState<KeyValuePair[]>(Object.entries(currentEnvVars).map(([key, value]) => ({ key, value })))
    const [hasChanges, setHasChanges] = useState(false)
    const [isSaving, setIsSaving] = useState(false)
    const [globalSystemPrompt, setGlobalSystemPrompt] = useState(savedGlobalSystemPrompt)
    const [globalSystemPromptHasChanges, setGlobalSystemPromptHasChanges] = useState(false)

    // Update local state when store changes externally
    useEffect(() => {
        const storeEnvVars = personalSettings?.settings.current.envVars ?? {}
        setEnvVarPairs(Object.entries(storeEnvVars).map(([key, value]) => ({ key, value })))
        setHasChanges(false)
    }, [personalSettings?.settings.current.envVars])

    useEffect(() => {
        setGlobalSystemPrompt(savedGlobalSystemPrompt)
        setGlobalSystemPromptHasChanges(false)
    }, [savedGlobalSystemPrompt])

    const handleEnvVarsChange = (pairs: KeyValuePair[]) => {
        setEnvVarPairs(pairs)
        setHasChanges(true)
    }

    const handleSave = () => {
        if (!personalSettings) return

        setIsSaving(true)
        try {
            // Convert pairs back to Record, filtering out empty keys
            const envVarsObj = envVarPairs.reduce(
                (acc, { key, value }) => {
                    if (key.trim()) {
                        acc[key.trim()] = value
                    }
                    return acc
                },
                {} as Record<string, string>
            )

            personalSettings.settings.set({ envVars: envVarsObj })
            setHasChanges(false)
        } finally {
            setIsSaving(false)
        }
    }

    const handleReset = () => {
        const storeEnvVars = personalSettings?.settings.current.envVars ?? {}
        setEnvVarPairs(Object.entries(storeEnvVars).map(([key, value]) => ({ key, value })))
        setHasChanges(false)
    }

    const handleGlobalSystemPromptSave = () => {
        if (!personalSettings) return
        const normalized = globalSystemPrompt.trim()
        personalSettings.settings.set({ globalSystemPrompt: normalized })
        setGlobalSystemPrompt(normalized)
        setGlobalSystemPromptHasChanges(false)
    }

    const handleGlobalSystemPromptReset = () => {
        setGlobalSystemPrompt(savedGlobalSystemPrompt)
        setGlobalSystemPromptHasChanges(false)
    }

    return (
        <div className="flex flex-col gap-8">
            <section>
                <h3 className="mb-1 text-base font-semibold text-base-content">Agent permissions</h3>
                <p className="mb-4 text-sm text-muted">
                    Choose how writable agent turns handle operations that require provider approval. Read-only turns always stay read-only.
                </p>
                <div className="grid gap-2" role="radiogroup" aria-label="Agent permission mode">
                    {permissionModeOptions.map((option) => {
                        const selected = (personalSettings?.settings.current.permissionMode ?? "yolo") === option.value
                        return (
                            <button
                                key={option.value}
                                type="button"
                                role="radio"
                                aria-checked={selected}
                                onClick={() => personalSettings?.settings.set({ permissionMode: option.value })}
                                className={`btn flex min-h-14 items-start justify-between gap-4 border p-3 text-left ${
                                    selected ? "border-primary bg-primary/10" : "border-border bg-base-200/40 hover:bg-base-200"
                                }`}
                            >
                                <span>
                                    <span className="block text-sm font-medium text-base-content">{option.label}</span>
                                    <span className="mt-1 block text-xs font-normal text-muted">{option.description}</span>
                                </span>
                                {selected && <CheckCircle size={16} className="mt-0.5 shrink-0 text-primary" />}
                            </button>
                        )
                    })}
                </div>
            </section>

            <section>
                <h3 className="text-base font-semibold text-base-content mb-1">
                    <label htmlFor="global-system-prompt">Global system prompt</label>
                </h3>
                <p id="global-system-prompt-description" className="text-sm text-muted mb-4">
                    Applied once when a new task starts and copied to every child agent thread in that task. Existing tasks keep the instructions they started
                    with.
                </p>
                <textarea
                    id="global-system-prompt"
                    aria-describedby="global-system-prompt-description global-system-prompt-count"
                    value={globalSystemPrompt}
                    maxLength={GLOBAL_SYSTEM_PROMPT_MAX_LENGTH}
                    onChange={(event) => {
                        setGlobalSystemPrompt(event.target.value)
                        setGlobalSystemPromptHasChanges(true)
                    }}
                    placeholder="Add instructions for every new agent thread…"
                    className="min-h-48 w-full resize-y border border-border bg-input px-3 py-2 font-mono text-sm leading-5 text-base-content outline-none focus:border-primary"
                />
                <div id="global-system-prompt-count" className="mt-1 text-right text-xs text-muted">
                    {globalSystemPrompt.length.toLocaleString()} / {GLOBAL_SYSTEM_PROMPT_MAX_LENGTH.toLocaleString()}
                </div>
                {globalSystemPromptHasChanges && (
                    <div className="flex gap-2 mt-3">
                        <button
                            type="button"
                            onClick={handleGlobalSystemPromptReset}
                            className="btn min-h-10 py-2 px-4 bg-base-200 hover:bg-base-300 text-base-content font-medium transition-colors border border-border max-[600px]:min-h-11"
                        >
                            Reset
                        </button>
                        <button
                            type="button"
                            onClick={handleGlobalSystemPromptSave}
                            className="btn min-h-10 py-2 px-4 bg-primary hover:bg-primary/90 text-primary-content font-medium transition-colors max-[600px]:min-h-11"
                        >
                            Save prompt
                        </button>
                    </div>
                )}
            </section>

            {/* Harnesses Section */}
            <HarnessStatusSection />

            {/* Runtime Binaries Section */}
            <RuntimeBinariesSection />

            {/* Environment Variables Section */}
            <section>
                <h3 className="text-base font-semibold text-base-content mb-1">Environment Variables</h3>
                <p className="text-sm text-muted mb-4">
                    Custom environment variables that propagate to all subprocesses, Claude queries, and terminal sessions.
                </p>

                <KeyValueEditor pairs={envVarPairs} onChange={handleEnvVarsChange} keyPlaceholder="VARIABLE_NAME" valuePlaceholder="value" secretMode />

                {/* Save/Reset buttons */}
                {hasChanges && (
                    <div className="flex gap-2 mt-4">
                        <button
                            type="button"
                            onClick={handleReset}
                            disabled={isSaving}
                            className="btn min-h-10 py-2 px-4 bg-base-200 hover:bg-base-300 text-base-content font-medium transition-colors border border-border disabled:opacity-50 max-[600px]:min-h-11"
                        >
                            Reset
                        </button>
                        <button
                            type="button"
                            onClick={handleSave}
                            disabled={isSaving}
                            className="btn min-h-10 py-2 px-4 bg-primary hover:bg-primary/90 text-primary-content font-medium transition-colors disabled:opacity-50 max-[600px]:min-h-11"
                        >
                            {isSaving ? "Saving..." : "Save Changes"}
                        </button>
                    </div>
                )}
            </section>

            {/* Telemetry Section */}
            <section>
                <h3 className="text-base font-semibold text-base-content mb-1">Telemetry</h3>
                <p className="text-sm text-muted mb-4">
                    Help improve the app by sending anonymous usage data. We track feature usage frequency and interaction types to help us decide what features
                    to prioritize. No code, prompts, file contents, or personal information is ever collected.
                </p>
                <label className="flex items-center gap-3 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={!(personalSettings?.settings.current.telemetryDisabled ?? false)}
                        onChange={(e) => personalSettings?.settings.set({ telemetryDisabled: !e.target.checked })}
                        className="checkbox checkbox-sm bg-base-200 border-border"
                    />
                    <span className="text-sm text-base-content">Enable anonymous telemetry</span>
                </label>
            </section>
        </div>
    )
})
